/** the axios response shape the pages destructure */
export interface ApiResponse<T> {
  data: T;
}

export type StateMappingType = "ORIENTATION" | "SENSOR";
export type StateMappingMode = "FORM" | "SCRIPT";
export type StateMappingScopeType =
  | "ALL"
  | "ASSET_TYPES"
  | "TAGS"
  | "TRACKER_TYPES";

export interface NamedRefDto {
  id: number;
  name?: string;
}

export interface StateMappingScopeDto {
  type: StateMappingScopeType;
  assetTypes: NamedRefDto[];
  tags: NamedRefDto[];
  trackerTypes: string[];
}

export interface MappedStateDto {
  key: string;
  /** what users see who have no translation in their language */
  label: string;
  image?: string | null;
  /** the label by language code ("de" -> "Offen") */
  translations?: Record<string, string> | null;
  /** R5: the key of its color of the palette (stateColorUtils); null when it has none */
  color?: string | null;
}

/** how a row of a sensor mapping's form tests the value */
export type SensorRangeOperator = "BETWEEN" | "EQ" | "LT" | "LE" | "GT" | "GE";

/**
 * one row of a sensor mapping's form. BETWEEN: from included, to excluded,
 * null = no limit; the other tests compare with value
 */
export interface SensorRangeDto {
  operator: SensorRangeOperator;
  from: number | null;
  to: number | null;
  value?: number | null;
  state: string;
}

export interface StateMappingDto {
  id?: number;
  type: StateMappingType;
  name: string;
  description?: string;
  priority?: number;
  scope: StateMappingScopeDto;
  mode: StateMappingMode;
  /** FORM mode of an orientation mapping */
  stateBySide: Record<string, string>;
  /** FORM mode of a sensor mapping */
  ranges: SensorRangeDto[];
  /** FORM mode: the state of any other value (orientation: an orientation that is no side); null: none */
  otherState?: string | null;
  script?: string;
  states: MappedStateDto[];
  /** sensor mappings: the SupportedChannelMeasurementType they translate */
  measurement?: string | null;
  trackerCount?: number;
  createdAt?: string;
  lastUpdatedAt?: string;
}

export interface StateMappingTestRequestDto {
  mapping: StateMappingDto;
  /** orientation mappings */
  orientation?: string;
  roll?: number;
  yaw?: number;
  pitch?: number;
  /** sensor mappings */
  value?: number;
  prevValue?: number | null;
  prevState?: string | null;
}

export interface StateMappingTestResultDto {
  key: string | null;
  label: string | null;
  image: string | null;
  declared: boolean;
  /** sensor mappings: the value the script stores */
  translatedValue?: number | null;
}

/** one measurement the organisation's linked sensors report (editor helper table) */
export interface FleetMeasurementDto {
  measurement: string;
  unit: string;
  products: string[];
  sensors: number;
  trackers: number;
  slots: number[];
  mappedById?: number | null;
  mappedByName?: string | null;
}

export interface StateMappingSummaryDto {
  unmappedTrackers: number;
}

export interface TrackerCountDto {
  trackers: number;
}

/** one sensor slot of a tracker: what it measures, the mapping in use and its current state */
export interface DeviceSensorSlotDto {
  /** the slot's own states when it overrides the organization default */
  ownStates?: MappedStateDto[];
  index: number;
  linked: boolean;
  name?: string | null;
  measurement?: string | null;
  unit?: string | null;
  mappingId?: number | null;
  mappingName?: string | null;
  mappingIndex?: number | null;
  mappingTotal?: number | null;
  matchedBy?: StateMappingScopeType | null;
  matchedAssetType?: NamedRefDto | null;
  matchedTag?: NamedRefDto | null;
  matchedTrackerType?: string | null;
  stateKey?: string | null;
  stateLabel?: string | null;
  stateImage?: string | null;
  stateColor?: string | null;
  value?: number | null;
  lastUpdated?: string | null;
  /** the states of the mapping in use, for charts and legends */
  states?: MappedStateDto[];
  /** how the mapping in use translates: its rows (FORM) or its script (SCRIPT) */
  mappingMode?: StateMappingMode | null;
  /** FORM: the rows of the mapping in use, in order */
  ranges?: SensorRangeDto[] | null;
  /** FORM: the state of any other value; null: none */
  otherState?: string | null;
  /** SCRIPT: the script of the mapping in use */
  script?: string | null;
}

export interface DeviceSensorMappingDto {
  slots: DeviceSensorSlotDto[];
}

export interface DeviceStateMappingDto {
  mappingId?: number | null;
  mappingName?: string;
  mappingDescription?: string | null;
  index?: number;
  total?: number;
  matchedBy?: StateMappingScopeType;
  matchedAssetType?: NamedRefDto;
  matchedTag?: NamedRefDto;
  matchedTrackerType?: string;
  states: MappedStateDto[];
  stateBySide?: Record<string, string>;
  script?: string;
  /** the tracker's current state, labelled by the mapping that produced it; stateAt: when it was reported */
  stateKey?: string | null;
  stateLabel?: string | null;
  stateImage?: string | null;
  stateColor?: string | null;
  stateAt?: string | null;
}

export const ORIENTATION_SIDES = [
  "top_up",
  "bottom_up",
  "front_up",
  "back_up",
  "left_up",
  "right_up",
] as const;
export type OrientationSide = (typeof ORIENTATION_SIDES)[number];

/** the product-sheet measurements a sensor mapping may translate, with their default unit */
export const MEASUREMENTS: { value: string; unit: string }[] = [
  { value: "temperature", unit: "°C" },
  { value: "humidity", unit: "%" },
  { value: "distancecm", unit: "cm" },
  { value: "eventcount", unit: "" },
  { value: "switchState", unit: "" },
  { value: "presence", unit: "" },
  { value: "orientation", unit: "" },
  { value: "activeTime", unit: "" },
  { value: "voltage", unit: "V" },
  { value: "amplitude", unit: "" },
  { value: "rotation", unit: "" },
  { value: "pressure", unit: "" },
  { value: "ampHour", unit: "Ah" },
];
